import type React from "react";
import type { Book } from "../../../app/books";

import { Link } from "@tanstack/react-router";
import { Tag } from "lucide-react";
import { useTranslation } from "react-i18next";

type MainInfoProps = {
  book: Book;
};

export const MainInfo: React.FC<MainInfoProps> = ({ book }) => {
  const { t } = useTranslation();

  return (
    <div className="dui-card-body">
      <h1 className="dui-card-title mb-2 text-4xl">{book.title}</h1>
      <p className="text-base-content/70 mb-4 flex gap-3 text-xl">
        {book.author.map((author) => (
          <Link to="/books" key={author.id} search={{ q: "author:" + author.name }}>
            {author.name}
          </Link>
        ))}
      </p>

      {(book.series || book.universe) && (
        <div className="mb-4 flex gap-3">
          {book.series?.map((series, index) => (
            <Link
              to="/books"
              key={index}
              search={{ q: "series:" + series.name }}
              className="dui-badge dui-badge-primary dui-badge-lg whitespace-nowrap"
            >
              {t("book.maininfo.series", {
                ...series,
                context: series.position ? "withPosition" : undefined
              })}
            </Link>
          ))}
          {book.universe?.map((universe, index) => (
            <Link
              to="/books"
              key={index}
              search={{ q: "universe:" + universe }}
              className="dui-badge dui-badge-secondary dui-badge-lg whitespace-nowrap"
            >
              {universe}
            </Link>
          ))}
        </div>
      )}

      {book.description && (
        <div className="mb-4">
          <h3 className="mb-2 font-semibold">{t("book.maininfo.description")}</h3>
          <p className="text-base-content/80 leading-relaxed">{book.description}</p>
        </div>
      )}

      {book.subjects && (
        <div className="mb-6">
          <h3 className="mb-2 flex items-center gap-2 font-semibold">
            <Tag className="h-4 w-4" />
            {t("book.maininfo.subjects")}
          </h3>
          <div className="flex flex-wrap gap-2">
            {(book.subjects ?? []).map((subject, index) => (
              <div key={index} className="dui-badge dui-badge-outline">
                {subject}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
